/**
 * O checklist de acessórios da vistoria (at-64, frente D/D2).
 *
 * ## Por que existe
 *
 * A lista dos 23 acessórios vivia **dentro da view** (`ACESSORIOS_PLAN` em
 * `views/checklistAcessorios/ChecklistAcessorios.tsx`), e o agrupamento por
 * status (`groupChecklist`) estava copiado em **duas** telas — o laudo e a
 * prévia. É a convenção da at-24: regra repetida em duas telas é divergência com
 * data marcada.
 *
 * 📌 E havia um agravante: o `checklistAcessoriosSlice` nascia com **46 entradas
 * que são legendas de FOTO** (as mesmas de `vistoriaLegendas.ts`, carro + moto),
 * substituídas no primeiro render pelo `useEffect` da tela. Aquele dado morto era
 * o que alimentava o bug do laudo — retomar uma vistoria pela `/carteira` e
 * finalizar gravava os 46 nulos por cima das 23 respostas salvas. O slice passou
 * a nascer **vazio**, e esta é a única lista.
 *
 * ⚠️ Funções e dados **puros**, sem React e sem rede.
 */

/** Uma resposta possível para um acessório. Espelha o enum do backend. */
export type StatusAcessorio = 'BOM' | 'RUIM' | 'NAO_EXISTENTE';

/** Um item do checklist, no formato que vai ao banco. */
export interface ItemChecklist {
  key: string;
  label: string;
  tipo: 'carro' | 'moto';
  status: StatusAcessorio | null;
}

/**
 * Os 23 acessórios do checklist, na ordem em que a tela os mostra.
 *
 * 🔴 **`key` e `label` são CONTRATO — não alterar nenhum dos dois.** Eles vão no
 * Json `accessories.items` do `PATCH /vistorias/:id/accessories`, de lá para o
 * PDF do laudo e para o SGA. Trocar uma `key` quebraria a reidratação de toda
 * vistoria já salva (o merge casa por `key`); trocar um `label` mudaria o texto
 * de documentos já emitidos.
 *
 * ⚠️ **`tipo: 'carro'` em todos, e não se filtra por ele.** Hoje moto responde os
 * mesmos 23 itens (inclusive "TETO SOLAR"), e mudar isso é decisão de produto —
 * a vistoria de motocicleta elétrica com checklist próprio é a frente C do item
 * 50 do backlog. O campo é preservado porque **está no Json que já foi gravado**.
 */
export const ACESSORIOS_VISTORIA = [
  { key: 'alto_falante', label: 'ALTO FALANTE', tipo: 'carro' },
  { key: 'radio_px', label: 'RÁDIO PX', tipo: 'carro' },
  { key: 'radio_am_fm', label: 'RÁDIO AM/FM', tipo: 'carro' },
  { key: 'radio_cd_player', label: 'RÁDIO CD PLAYER', tipo: 'carro' },
  { key: 'alarme_sonoro', label: 'ALARME SONORO', tipo: 'carro' },
  { key: 'chave_code', label: 'CHAVE CODE', tipo: 'carro' },
  { key: 'trava_carneiro', label: 'TRAVA CARNEIRO', tipo: 'carro' },
  { key: 'trava_multilock', label: 'TRAVA MULTILOCK', tipo: 'carro' },

  { key: 'aerofolio', label: 'AEROFÓLIO', tipo: 'carro' },
  { key: 'cambio_automatico', label: 'CAMBIO AUTOMÁTICO', tipo: 'carro' },
  { key: 'computador_de_bordo', label: 'COMPUTADOR DE BORDO', tipo: 'carro' },
  { key: 'banco_eletrico', label: 'BANCO ELÉTRICO', tipo: 'carro' },
  { key: 'banco_de_couro', label: 'BANCO DE COURO', tipo: 'carro' },
  { key: 'conta_giros', label: 'CONTA GIROS', tipo: 'carro' },
  { key: 'desembacador_traseiro', label: 'DESEMBAÇADOR TRASEIRO', tipo: 'carro' },
  { key: 'direcao_hidraulica', label: 'DIREÇÃO HIDRÁULICA', tipo: 'carro' },

  { key: 'engate_de_reboque', label: 'ENGATE DE REBOQUE', tipo: 'carro' },
  { key: 'limpador_traseiro', label: 'LIMPADOR TRASEIRO', tipo: 'carro' },
  { key: 'retrovisor_eletrico', label: 'RETROVISOR ELÉTRICO', tipo: 'carro' },
  { key: 'roda_liga_leve', label: 'RODA LIGA LEVE', tipo: 'carro' },
  { key: 'teto_solar', label: 'TETO SOLAR', tipo: 'carro' },
  { key: 'trava_eletrica', label: 'TRAVA ELÉTRICA', tipo: 'carro' },
  { key: 'cilindro_de_gas', label: 'CILINDRO DE GÁS', tipo: 'carro' },
] as const satisfies ReadonlyArray<{ key: string; label: string; tipo: 'carro' | 'moto' }>;

/**
 * As 3 respostas possíveis, no shape que o `RadioCards` do kit consome.
 *
 * ⚠️ O CSS antigo dava **cor por opção** à escolhida (Ruim vermelho, Não existe
 * cinza). Isso saiu: o `RadioCards` marca a escolha com borda e fundo da marca, e
 * o **texto** já diz qual é (spec 02 §9 — marcador nunca depende só de cor). A
 * cor por grupo continua no **laudo**, onde é resumo do resultado e não escolha.
 */
export const OPCOES_CHECKLIST = [
  { valor: 'BOM', rotulo: 'Bom' },
  { valor: 'RUIM', rotulo: 'Ruim' },
  { valor: 'NAO_EXISTENTE', rotulo: 'Não existe' },
] as const;

/** O checklist agrupado por resposta, em RÓTULOS — o que o documento exibe. */
export interface ChecklistAgrupado {
  bom: string[];
  ruim: string[];
  naoExistente: string[];
}

/** Rótulo canônico de uma chave — usado quando o banco não gravou o `label`. */
const ROTULO_POR_CHAVE = new Map<string, string>(
  ACESSORIOS_VISTORIA.map((acessorio) => [acessorio.key, acessorio.label]),
);

/**
 * Agrupa as respostas por status, devolvendo os **rótulos**.
 *
 * 🔴 Devolve `string[]` de propósito: é exatamente o shape que
 * `GET /vistorias/carro/:id/ultima` já retorna (`checklist.bom[]`), então as três
 * telas que renderizam o documento entregam a mesma coisa ao `DocumentoVistoria`
 * — uma vinda do Redux, outra da API, e nenhuma precisa converter.
 *
 * ⚠️ Item **sem resposta não entra em grupo nenhum**: ele é pendência da tela de
 * checklist, não um resultado. Era o comportamento das duas cópias.
 *
 * ⚠️ **`label` pode faltar** — o tipo de `GET /vistorias/:id` o declara opcional,
 * e o Json de uma vistoria antiga pode não tê-lo. Nesse caso o rótulo é resolvido
 * pela `key` na lista canônica; só se nem isso existir a `key` é exibida crua.
 * Melhor um nome técnico no documento que uma linha em branco.
 */
export function agruparChecklist(
  itens: ReadonlyArray<{ key?: string; label?: string | null; status?: string | null }>,
): ChecklistAgrupado {
  const agrupado: ChecklistAgrupado = { bom: [], ruim: [], naoExistente: [] };

  for (const item of itens) {
    const rotulo =
      item.label?.trim() ||
      (item.key ? ROTULO_POR_CHAVE.get(item.key) : undefined) ||
      item.key ||
      '';
    if (!rotulo) continue;

    if (item.status === 'BOM') agrupado.bom.push(rotulo);
    else if (item.status === 'RUIM') agrupado.ruim.push(rotulo);
    else if (item.status === 'NAO_EXISTENTE') agrupado.naoExistente.push(rotulo);
  }

  return agrupado;
}

/**
 * Junta a lista canônica com as respostas que já existem, preservando-as.
 *
 * É o merge que a tela de checklist fazia inline, em dois lugares (no mount, a
 * partir do Redux; e na reidratação, a partir do banco). Aqui não há `as any`: a
 * lista é tipada e o retorno é `ItemChecklist[]`.
 */
export function mesclarRespostas(
  respostas: ReadonlyArray<{ key: string; status?: string | null }>,
): ItemChecklist[] {
  const porChave = new Map(respostas.map((item) => [item.key, item.status ?? null]));

  return ACESSORIOS_VISTORIA.map((acessorio) => ({
    key: acessorio.key,
    label: acessorio.label,
    tipo: acessorio.tipo,
    status: (porChave.get(acessorio.key) as StatusAcessorio | null) ?? null,
  }));
}
